import React, { useState, useEffect } from "react";
import { postData, uploadFile, getData } from "../../../webService/webservice.js";
import { toast } from "react-toastify";
import TVFMLoader from "../Loader/loader.jsx";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import ServiceCategorySelect from "../../Challange/ServiceCategorySelect.jsx";
import ServiceSubCategorySelect from "../../Challange/ServiceSubCategorySelect.jsx";
import { Icon } from "@iconify/react";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import StockImagePicker from "../../../components/Common/ImagePicker/StockImagePicker.jsx";
import AddServiceCategoryForm from "../ServiceCategory/AddServiceCategoryForm.jsx";
import AddSubCategoryForm from "../ServiceSubCategory/AddSubCategoryForm.jsx";

const AddServiceForm = ({ onSuccess, onCancel, initialStoreId, hideAddCategories = false }) => {
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const [serviceImage, setServiceImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [errors, setErrors] = useState({});
    const [stores, setStores] = useState([]);
    const [uploadMethod, setUploadMethod] = useState("Normal");
    const [showStockPicker, setShowStockPicker] = useState(false);
    const [selectedStockImage, setSelectedStockImage] = useState(null);
    const sessionToken = localStorage.getItem("Token");
    const [isAddCategory, setIsAddCategory] = useState(false);
    const [isAddSubCategory, setIsAddSubCategory] = useState(false);
    const [categoryRefreshKey, setCategoryRefreshKey] = useState(0);
    const [subCategoryRefreshKey, setSubCategoryRefreshKey] = useState(0);
    const [isGSTVerified, setIsGSTVerified] = useState(false);
    const [formData, setFormData] = useState({
        storeId: initialStoreId || "",
        serviceName: '',
        serviceCode: '',
        serviceCategory: '',
        serviceCategoryId: '',
        serviceCategoryName: '',
        serviceSubCategoryId: '',
        serviceSubCategoryName: '',
        serviceStatus: 'Active',
        gstPercentage: "",
        gstAmount: "",
        duration: "",
        nonMemberShipPrice: "",
        memberShipPrice: "",
    });

    useEffect(() => {
        if (initialStoreId) {
            const fetchStoreLabel = async () => {
                try {
                    const token = localStorage.getItem("Token");
                    const res = await getData(`stores/store/${initialStoreId}`, {}, token);
                    if (res?.data) {
                        setStores([{ value: res.data._id, label: res.data.storeName }]);
                    }
                    const gstValid = res.data?.gstData?.valid === true;
                    setIsGSTVerified(gstValid);
                } catch (err) {
                    console.error("Failed to fetch initial store label", err);
                }
            };
            fetchStoreLabel();
        }
    }, [initialStoreId]);

    const EMPTY_ARRAY = [];

    const calculateGSTAmount = (price, gst) => {
        const base = Number(price || 0);
        const gstPercent = Number(gst || 0);
        return ((base * gstPercent) / 100).toFixed(2);
    };

    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const res = await getData(
                "stores/stores",
                { page, limit: 10, search: inputValue },
                sessionToken
            );

            const results = res?.data?.results || EMPTY_ARRAY;

            const options = results.map(store => ({
                value: store._id,
                label: store.storeName
            }));

            return {
                options,
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Store Fetch Failed", err);
            return { options: [], hasMore: false };
        }
    };

    const loadServiceCategories = async (inputValue, loadedOptions, { page }) => {
        try {
            const params = {
                page,
                limit: 10,
                search: inputValue,
                storeId: formData.storeId
            };
            const token = localStorage.getItem("Token");
            const res = await getData('servicecategories/service-categories', params, token);
            const results = res?.data?.results?.[0]?.paginatedResults || [];
            const options = results.map(cat => ({ value: cat._id, label: cat.serviceCategoryName }));
            return { options, hasMore: results.length === 10, additional: { page: page + 1 } };
        } catch (err) {
            return { options: [], hasMore: false };
        }
    };

    const loadServiceSubCategories = async (inputValue, loadedOptions, { page }, categoryId) => {
        try {
            const params = {
                page,
                limit: 10,
                search: inputValue,
                serviceCategoryId: categoryId, // Filter by Category
                storeId: formData.storeId
            };
            const token = localStorage.getItem("Token");
            const res = await getData('servicesubcategories/service-subcategories', params, token);
            let results = res?.data?.results?.[0]?.paginatedResults || [];
            if (categoryId) {
                results = results.filter(sub => {
                    const subCatId = sub.serviceCategoryId?._id || sub.serviceCategoryId;
                    return subCatId === categoryId;
                });
            }
            const options = results.map(sub => ({ value: sub._id, label: sub.serviceSubCategoryName }));
            return { options, hasMore: results.length === 10, additional: { page: page + 1 } };
        } catch (err) {
            return { options: [], hasMore: false };
        }
    };

    const handleRemoveImage = () => {
        setPreviewUrl(null);
        setServiceImage(null);
        setSelectedStockImage(null);
    };

    const handleStockSelect = (imageObject) => {
        if (!imageObject) return;
        setSelectedStockImage(imageObject);
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        const uri = imageObject.uri || imageObject.fileObject?.uri || "";
        if (uri) {
            setPreviewUrl(`${baseUrl}/${uri.replace(/^\/+/, "")}`);
        }
        setShowStockPicker(false);
        setErrors(prev => ({ ...prev, serviceImage: null }));
    };

    const validateFields = (data, method, img, stockImg) => {
        let newErrors = {};
        if (!data.storeId) newErrors.storeId = "Store is required";
        if (!data.serviceName?.trim()) newErrors.serviceName = "Service Name is required";
        if (!data.serviceCategoryId) newErrors.serviceCategoryId = "Service Category is required";
        if (!data.serviceSubCategoryId) newErrors.serviceSubCategoryId = "Service Sub Category is required";
        if (!data.nonMemberShipPrice) newErrors.nonMemberShipPrice = "Price is required";

        if (method === "Normal" && img) {
            const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
            const allowedExtensions = ["jpg", "jpeg", "png"];
            const fileExtension = img.name.split('.').pop().toLowerCase();
            const isValidType = allowedTypes.includes(img.type) || allowedExtensions.includes(fileExtension);
            if (!isValidType) {
                newErrors.serviceImage = "Only JPEG, JPG, and PNG images are allowed.";
            } else if (img.size > 1024 * 1024) {
                newErrors.serviceImage = "Image size exceeds 1MB. Please select an image smaller than 1MB.";
            }
        }

        return newErrors;
    };

    const handleAddService = async (e) => {
        e.preventDefault();
        const validationErrors = validateFields(formData, uploadMethod, serviceImage, selectedStockImage);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setShowLoader(true);
        try {
            const token = localStorage.getItem("Token");
            const userId = localStorage.getItem("userId");

            let serviceImageObject = null;

            if (uploadMethod === "Normal") {
                if (serviceImage && userId) {
                    const uploadResult = await uploadFile(`files/upload/${userId}`, serviceImage);
                    if (uploadResult?.status === 200) {
                        serviceImageObject = uploadResult.data;
                    } else {
                        toast.error(uploadResult?.message || "Image upload failed");
                        setShowLoader(false);
                        return;
                    }
                }
            } else {
                serviceImageObject = selectedStockImage;
            }

            const serviceData = {
                storeId: formData.storeId,
                serviceName: formData.serviceName,
                serviceCode: formData.serviceCode,
                serviceCategory: formData.serviceCategoryId,
                serviceSubCategory: formData.serviceSubCategoryId,
                serviceStatus: formData.serviceStatus,
                memberShipPrice: Number(formData.memberShipPrice || 0),
                gstPercentage: Number(formData.gstPercentage || 0),
                gstAmount: Number(formData.gstAmount || 0),
                duration: Number(formData.duration),
                nonMemberShipPrice: Number(formData.nonMemberShipPrice),
                serviceImageObject: serviceImageObject,
                imageSource: uploadMethod
            };
            const response = await postData("services/", serviceData, token);
            toast.success(response.message || "Service added successfully");
            onSuccess(response.data);

        } catch (error) {
            console.error("Add Service Error:", error);
            toast.error(error.message || "Add Service Failed");
        } finally {
            setShowLoader(false);
        }
    };

    return (
        <div className="add-category-popup-form">
            {showLoader && (
                <div className="scissor-overlay">
                    <div className="scissor-loader"><TVFMLoader /></div>
                </div>
            )}
            <h3>Add Service</h3>
            <form className="category-form" onSubmit={handleAddService}>
                <div className="category-form-row">
                    <div className="category-form-group">
                        <label className="input-label">Branch Name</label>
                        <StoreMultiSelect
                            value={stores}
                            onChange={(val) => {
                                if ((role === "StoreAdmin" || role === "StoreManager") && adminStoreIds.length === 1) return;
                                setStores(val);
                                setFormData(prev => ({
                                    ...prev,
                                    storeId: val && val.length > 0 ? val[0].value : "",
                                    serviceCategoryId: "",
                                    serviceCategoryName: "",
                                    serviceSubCategoryId: "",
                                    serviceSubCategoryName: ""
                                }));
                                setCategoryRefreshKey(prev => prev + 1);
                                setSubCategoryRefreshKey(prev => prev + 1);
                            }}
                            loadStores={loadStores}
                            loggedInRole={role}
                            assignedStoreIds={adminStoreIds}
                            isMulti={false}
                        />
                        {errors.storeId && <p className="field-error">{errors.storeId}</p>}
                    </div>
                    <div className="category-form-group">
                        <label className="input-label">Service Name</label>
                        <input
                            type="text"
                            placeholder="Enter service name"
                            className="category-input"
                            value={formData.serviceName}
                            onChange={e => setFormData(prev => ({ ...prev, serviceName: e.target.value }))}
                        />
                        {errors.serviceName && <p className="field-error">{errors.serviceName}</p>}
                    </div>
                </div>

                <div className="category-form-row">
                    <div className="category-form-group">
                        <label className="input-label">Service Category</label>
                        <ServiceCategorySelect
                            key={categoryRefreshKey}
                            cacheUniqs={[categoryRefreshKey]}
                            value={formData.serviceCategoryId ? { value: formData.serviceCategoryId, label: formData.serviceCategoryName } : null}
                            onChange={(val) => {
                                setFormData(prev => ({
                                    ...prev,
                                    serviceCategoryId: val?.value || "",
                                    serviceCategoryName: val?.label || "",
                                    serviceSubCategoryId: "",
                                    serviceSubCategoryName: ""
                                }));
                            }}
                            loadServiceCategories={loadServiceCategories}
                            onAddServiceCategory={hideAddCategories ? undefined : () => setIsAddCategory(true)}
                        />
                        {errors.serviceCategoryId && <p className="field-error">{errors.serviceCategoryId}</p>}
                    </div>
                    <div className="category-form-group">
                        <label className="input-label">Service Sub Category</label>
                        <ServiceSubCategorySelect
                            cacheUniqs={[subCategoryRefreshKey, formData.serviceCategoryId]}
                            serviceCategoryId={formData.serviceCategoryId}
                            value={formData.serviceSubCategoryId ? { value: formData.serviceSubCategoryId, label: formData.serviceSubCategoryName } : null}
                            onChange={(val) => {
                                setFormData(prev => ({
                                    ...prev,
                                    serviceSubCategoryId: val?.value || "",
                                    serviceSubCategoryName: val?.label || ""
                                }));
                            }}
                            loadServiceSubCategories={loadServiceSubCategories}
                            onAddServiceSubCategory={hideAddCategories ? undefined : () => setIsAddSubCategory(true)}
                            isDisabled={!formData.serviceCategoryId}
                        />
                        {errors.serviceSubCategoryId && <p className="field-error">{errors.serviceSubCategoryId}</p>}
                    </div>
                </div>

                <div className="category-form-row">

                    <div className="category-form-group">
                        <label className="input-label">Regular Price</label>
                        <input
                            type="number"
                            placeholder="Enter price"
                            className="category-input"
                            value={formData.nonMemberShipPrice}
                            onChange={e => setFormData(prev => ({ ...prev, nonMemberShipPrice: e.target.value }))}
                        />
                        {errors.nonMemberShipPrice && <p className="field-error">{errors.nonMemberShipPrice}</p>}
                    </div>
                    <div className="category-form-group">
                        <label className="input-label">MemberShip Price</label>
                        <input
                            type="text"
                            className="category-input"
                            value={formData.memberShipPrice || ""}
                            placeholder="Enter base price"
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                    setFormData(prev => ({
                                        ...prev,
                                        memberShipPrice: value
                                    }));
                                }
                            }}
                        />
                        {errors.memberShipPrice && (
                            <p className="field-error">{errors.memberShipPrice}</p>
                        )}
                    </div>
                </div>

                <div className="category-form-row">
                    <div className="category-form-group">
                        <label className="input-label">Duration (Minutes)</label>
                        <input
                            type="number"
                            placeholder="Enter duration"
                            className="category-input"
                            value={formData.duration}
                            onChange={e => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                        />
                    </div>
                    <div className="category-form-group">
                        <label className="input-label">GST Percentage (%)</label>
                        <select
                            className="category-input"
                            value={String(formData.gstPercentage ?? "")}
                            disabled={!isGSTVerified}
                            onChange={(e) => {
                                const gstPercentage = Number(e.target.value);
                                const price = Number(formData.nonMemberShipPrice || 0);
                                const gstAmount = calculateGSTAmount(
                                    price,
                                    gstPercentage
                                );

                                setFormData(prev => ({
                                    ...prev,
                                    gstPercentage,
                                    gstAmount
                                }));
                            }}
                        >
                            <option value="">Select GST</option>
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                        </select>
                    </div>
                </div>

                <div className="category-form-row">
                    <div className="category-form-group">
                        <label className="input-label">GST Amount</label>
                        <input
                            type="number"
                            placeholder="GST Amount"
                            className="category-input"
                            value={formData.gstAmount}
                            readOnly
                        />
                    </div>
                </div>



                <div className="category-form-row">
                    <div className="category-form-group" style={{ flex: '1 1 100%' }}>
                        <label className="input-label">Service Image (Max 1MB, JPEG/JPG/PNG only)</label>
                        <div className="upload-options" style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#525252' }}>
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    checked={uploadMethod === "Normal"}
                                    style={{ accentColor: 'linear-gradient(90deg, #181B23 0%, #1C202B 35%, #1A1E28 65%, #161A22 100%)' }}
                                    onChange={() => {
                                        setUploadMethod("Normal");
                                        setPreviewUrl(serviceImage ? URL.createObjectURL(serviceImage) : null);
                                    }}
                                />
                                Normal Upload
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#525252' }}>
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    checked={uploadMethod === "Stock"}
                                    style={{ accentColor: 'linear-gradient(90deg, #181B23 0%, #1C202B 35%, #1A1E28 65%, #161A22 100%)' }}
                                    onChange={() => {
                                        setUploadMethod("Stock");
                                        if (selectedStockImage) {
                                            const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
                                            const uri = selectedStockImage.uri || selectedStockImage.fileObject?.uri || "";
                                            setPreviewUrl(`${baseUrl}/${uri.replace(/^\/+/, "")}`);
                                        } else {
                                            setPreviewUrl(null);
                                        }
                                    }}
                                />
                                Stock Image
                            </label>
                        </div>

                        {uploadMethod === "Normal" ? (
                            <input
                                type="file"
                                accept=".jpeg,.jpg,.png"
                                className="category-input"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
                                        const allowedExtensions = ["jpg", "jpeg", "png"];
                                        const fileExtension = file.name.split('.').pop().toLowerCase();
                                        const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

                                        if (!isValidType) {
                                            setErrors(prev => ({ ...prev, serviceImage: "Only JPEG, JPG, and PNG images are allowed." }));
                                            toast.error("Only JPEG, JPG, and PNG images are allowed.");
                                            setServiceImage(null);
                                            setPreviewUrl(null);
                                            if (e.target) e.target.value = "";
                                        } else if (file.size > 1024 * 1024) {
                                            setErrors(prev => ({ ...prev, serviceImage: "Image size exceeds 1MB. Please select an image smaller than 1MB." }));
                                            toast.error("Image size exceeds 1MB. Please select an image smaller than 1MB.");
                                            setServiceImage(null);
                                            setPreviewUrl(null);
                                            if (e.target) e.target.value = "";
                                        } else {
                                            setServiceImage(file);
                                            setPreviewUrl(URL.createObjectURL(file));
                                            setErrors(prev => ({ ...prev, serviceImage: null }));
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="stock-picker-trigger">
                                <button
                                    type="button"
                                    className="select-stock-btn"
                                    onClick={() => setShowStockPicker(true)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                        justifyContent: 'flex-start',
                                        gap: '10px',
                                        fontSize: '14px',
                                        color: selectedStockImage ? '#333' : '#888'
                                    }}
                                >
                                    <Icon icon="mdi:image-search" style={{ fontSize: '18px', color: '#64748b' }} />
                                    {selectedStockImage ? `Selected: ${selectedStockImage.name || 'Stock Image'}` : "Click to select a stock image..."}
                                </button>
                            </div>
                        )}
                        {errors.serviceImage && <p className="field-error">{errors.serviceImage}</p>}
                        {previewUrl && (
                            <div className="image-preview" style={{ marginTop: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <div style={{ position: 'relative' }}>
                                    <img src={previewUrl} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            background: '#ef4444',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            zIndex: 5
                                        }}
                                        onClick={handleRemoveImage}
                                        title="Remove image"
                                    >
                                        <Icon icon="mdi:close" style={{ fontSize: '14px' }} />
                                    </div>
                                </div>
                                {uploadMethod === "Stock" && selectedStockImage && (
                                    <div style={{ paddingTop: '4px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', margin: '0' }}>Stock Image Selected</p>
                                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0' }}>{selectedStockImage.name}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="category-buttons">
                    <button className="add-Product-button" type="submit">Add Service</button>
                    <button className="cancel-Product-button" type="button" onClick={onCancel}>Cancel</button>
                </div>
            </form>
            {showStockPicker && (
                <StockImagePicker
                    onSelect={handleStockSelect}
                    onCancel={() => setShowStockPicker(false)}
                />
            )}
            {isAddCategory && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon icon={closeIcon} className="popup-close-icon-outside" onClick={() => setIsAddCategory(false)} />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <AddServiceCategoryForm
                                onSuccess={(newCategory) => {
                                    setIsAddCategory(false);
                                    if (newCategory) {
                                        let catData = Array.isArray(newCategory) ? newCategory[0] : newCategory;
                                        if (catData && catData.serviceCategory) catData = catData.serviceCategory;
                                        else if (catData && catData.category) catData = catData.category;

                                        const newId = catData._id || catData.id;
                                        const newName = catData.serviceCategoryName || catData.Name || catData.name || "New Category";
                                        setFormData(prev => ({
                                            ...prev,
                                            serviceCategoryId: newId,
                                            serviceCategoryName: newName,
                                            serviceSubCategoryId: "",
                                            serviceSubCategoryName: ""
                                        }));
                                    }
                                }}
                                onCancel={() => setIsAddCategory(false)}
                                initialStoreId={formData.storeId}
                            />
                        </div>
                    </div>
                </div>
            )}
            {isAddSubCategory && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon icon={closeIcon} className="popup-close-icon-outside" onClick={() => setIsAddSubCategory(false)} />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <AddSubCategoryForm
                                onSuccess={(newSub) => {
                                    setIsAddSubCategory(false);
                                    if (newSub) {
                                        let subData = Array.isArray(newSub) ? newSub[0] : newSub;
                                        if (subData && subData.serviceSubCategory) subData = subData.serviceSubCategory;
                                        else if (subData && subData.subCategory) subData = subData.subCategory;

                                        const newId = subData._id || subData.id;
                                        const newName = subData.serviceSubCategoryName || subData.name || "New SubCategory";
                                        setFormData(prev => ({
                                            ...prev,
                                            serviceSubCategoryId: newId,
                                            serviceSubCategoryName: newName
                                        }));
                                    }
                                    setSubCategoryRefreshKey(prev => prev + 1);
                                }}
                                onCancel={() => setIsAddSubCategory(false)}
                                initialStoreId={formData.storeId}
                                hideAddCategory={true}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddServiceForm;
